import { RailsAPIBase, type paths } from "../index.client";
import type { Simplify } from "type-fest";

// ============================================================================
// Type Definitions
// ============================================================================

export type WorkspacesListResponse = NonNullable<
  paths["/api/v1/workspaces"]["get"]["responses"][200]["content"]["application/json"]
>;

export type Workspace = WorkspacesListResponse["workspaces"][number];

export type CreateWorkspaceRequest = NonNullable<
  paths["/api/v1/workspaces"]["post"]["requestBody"]
>["content"]["application/json"];

export type CreateWorkspaceResponse = NonNullable<
  paths["/api/v1/workspaces"]["post"]["responses"][201]["content"]["application/json"]
>;

export type UpdateWorkspaceResponse = NonNullable<
  paths["/api/v1/workspaces/{id}"]["patch"]["responses"][200]["content"]["application/json"]
>;

export type DeleteWorkspaceResponse = NonNullable<
  paths["/api/v1/workspaces/{id}"]["delete"]["responses"][200]["content"]["application/json"]
>;

// ============================================================================
// Service Class
// ============================================================================

export class WorkspaceAPIService extends RailsAPIBase {
  constructor(options: Simplify<ConstructorParameters<typeof RailsAPIBase>[0]>) {
    super(options);
  }

  async list(): Promise<WorkspacesListResponse> {
    const client = await this.getClient();
    const response = await client.GET("/api/v1/workspaces");

    if (response.error) {
      throw new Error(`Failed to list workspaces: ${JSON.stringify(response.error)}`);
    }
    if (!response.data) {
      throw new Error("Failed to list workspaces: No data returned");
    }
    return response.data;
  }

  async create(name: string): Promise<CreateWorkspaceResponse> {
    const client = await this.getClient();
    const response = await client.POST("/api/v1/workspaces", {
      body: { workspace: { name } },
    });

    if (response.error) {
      const err = response.error as { errors?: string[] };
      throw new Error(err.errors?.[0] ?? "Failed to create workspace");
    }
    if (!response.data) {
      throw new Error("Failed to create workspace: No data returned");
    }
    return response.data;
  }

  async update(id: number, name: string): Promise<UpdateWorkspaceResponse> {
    const client = await this.getClient();
    const response = await client.PATCH("/api/v1/workspaces/{id}", {
      params: { path: { id } },
      body: { workspace: { name } },
    });

    if (response.error) {
      const err = response.error as { errors?: string[] };
      throw new Error(err.errors?.[0] ?? "Failed to rename workspace");
    }
    if (!response.data) {
      throw new Error("Failed to rename workspace: No data returned");
    }
    return response.data;
  }

  async destroy(id: number): Promise<DeleteWorkspaceResponse> {
    const client = await this.getClient();
    const response = await client.DELETE("/api/v1/workspaces/{id}", {
      params: { path: { id } },
    });

    if (response.error) {
      const err = response.error as { error?: string; errors?: string[] };
      throw new Error(err.error ?? err.errors?.[0] ?? "Failed to delete workspace");
    }
    if (!response.data) {
      throw new Error("Failed to delete workspace: No data returned");
    }
    return response.data;
  }
}
